import React, { useState, useEffect, useCallback } from 'react';
import {
  ValidationResult,
  runValidation,
  getValidationResult,
} from '../../services/adminApi';
import { Button, Spinner, StatusPill } from '../ui';

interface ValidationPreviewProps {
  month: string;
  /** Called when admin approves validation and wants to proceed to CM upload */
  onApprove?: () => void;
  /** Whether the approve button should be shown */
  showApprove?: boolean;
}

const ValidationPreview: React.FC<ValidationPreviewProps> = ({
  month,
  onApprove,
  showApprove = true,
}) => {
  const [result, setResult] = useState<ValidationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedChecks, setExpandedChecks] = useState<Set<string>>(new Set());
  const [acknowledgedWarnings, setAcknowledgedWarnings] = useState<Set<string>>(new Set());

  const fetchValidation = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { validation } = await getValidationResult(month);
      setResult(validation);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [month]);

  const handleRunValidation = async () => {
    setLoading(true);
    setError(null);
    try {
      const { validation } = await runValidation(month);
      setResult(validation);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchValidation();
  }, [fetchValidation]);

  const toggleExpand = (name: string) => {
    setExpandedChecks((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleAcknowledge = (name: string) => {
    setAcknowledgedWarnings((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const warnings = result?.checks.filter((c) => c.status === 'warning') || [];
  const allWarningsAcknowledged = warnings.every((w) => acknowledgedWarnings.has(w.name));
  const hasBlockers = (result?.summary.blockers || 0) > 0;
  const canApprove = result && !hasBlockers && allWarningsAcknowledged;

  if (loading && !result) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingText}>
          <Spinner label="Running validation checks…" />
        </div>
      </div>
    );
  }

  if (error && !result) {
    return (
      <div style={styles.container}>
        <div style={styles.errorText}>{error}</div>
        <Button variant="ghost" size="sm" onClick={handleRunValidation}>
          Retry Validation
        </Button>
      </div>
    );
  }

  if (!result) return null;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h3 style={styles.title}>Pre-Upload Validation</h3>
        <div style={styles.summaryBadges}>
          {result.summary.passed > 0 && (
            <StatusPill variant="ok" noDot>{result.summary.passed} passed</StatusPill>
          )}
          {result.summary.warnings > 0 && (
            <StatusPill variant="pending" noDot>{result.summary.warnings} warning{result.summary.warnings > 1 ? 's' : ''}</StatusPill>
          )}
          {result.summary.blockers > 0 && (
            <StatusPill variant="flagged" noDot>{result.summary.blockers} blocker{result.summary.blockers > 1 ? 's' : ''}</StatusPill>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRunValidation}
          disabled={loading}
        >
          {loading ? 'Running...' : 'Re-run'}
        </Button>
      </div>

      {/* Check list */}
      <div style={styles.checkList}>
        {result.checks.map((check) => (
          <div key={check.name} style={styles.checkItem}>
            <div
              style={styles.checkRow}
              onClick={() => check.details && toggleExpand(check.name)}
            >
              <span style={{
                ...styles.statusIcon,
                color: getCheckColor(check.status),
              }}>
                {getCheckIcon(check.status)}
              </span>
              <div style={styles.checkContent}>
                <span style={styles.checkName}>{check.name}</span>
                <span style={styles.checkMessage}>{check.message}</span>
              </div>
              {check.details && (
                <span style={styles.expandIcon}>
                  {expandedChecks.has(check.name) ? '\u25BC' : '\u25B6'}
                </span>
              )}
            </div>

            {/* Warning acknowledgment */}
            {check.status === 'warning' && (
              <label style={styles.acknowledgeLabel}>
                <input
                  type="checkbox"
                  checked={acknowledgedWarnings.has(check.name)}
                  onChange={() => toggleAcknowledge(check.name)}
                  style={styles.checkbox}
                />
                <span style={styles.acknowledgeText}>Acknowledged</span>
              </label>
            )}

            {/* Expanded details */}
            {expandedChecks.has(check.name) && check.details && (
              <div style={styles.detailsBox}>
                <pre style={styles.detailsPre}>
                  {JSON.stringify(check.details, null, 2)}
                </pre>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Approve button */}
      {showApprove && (
        <div style={styles.approveSection}>
          {hasBlockers && (
            <p style={styles.blockerMessage}>
              Resolve blockers before proceeding to Channel Meter upload.
            </p>
          )}
          {!hasBlockers && warnings.length > 0 && !allWarningsAcknowledged && (
            <p style={styles.warningMessage}>
              Acknowledge all warnings before proceeding.
            </p>
          )}
          <Button
            variant="primary"
            size="md"
            disabled={!canApprove}
            onClick={onApprove}
          >
            Approve and Proceed to CM Upload
          </Button>
        </div>
      )}
    </div>
  );
};

function getCheckIcon(status: string): string {
  switch (status) {
    case 'passed': return '\u2713';
    case 'warning': return '\u26A0';
    case 'blocker': return '\u2717';
    default: return '\u25CB';
  }
}

function getCheckColor(status: string): string {
  switch (status) {
    case 'passed': return 'var(--success)';
    case 'warning': return 'var(--warning)';
    case 'blocker': return 'var(--danger)';
    default: return 'var(--fg-3)';
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    background: 'var(--bg-surface-1)',
    border: '1px solid var(--border-1)',
    borderRadius: 'var(--radius-lg)',
    padding: '16px',
    marginTop: '12px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  title: {
    margin: 0,
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--fg-1)',
  },
  summaryBadges: {
    display: 'flex',
    gap: '6px',
    flex: 1,
  },
  checkList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  checkItem: {
    borderBottom: '1px solid var(--border-1)',
    paddingBottom: '8px',
  },
  checkRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    cursor: 'pointer',
  },
  statusIcon: {
    fontSize: '14px',
    fontWeight: 600,
    minWidth: '18px',
    textAlign: 'center' as const,
    marginTop: '1px',
  },
  checkContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
  },
  checkName: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--fg-1)',
  },
  checkMessage: {
    fontSize: '12px',
    color: 'var(--fg-2)',
    lineHeight: '1.4',
  },
  expandIcon: {
    fontSize: '10px',
    color: 'var(--fg-3)',
    marginTop: '2px',
  },
  acknowledgeLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginLeft: '26px',
    marginTop: '6px',
    cursor: 'pointer',
  },
  checkbox: {
    cursor: 'pointer',
  },
  acknowledgeText: {
    fontSize: '12px',
    color: 'var(--warning)',
    fontWeight: 500,
  },
  detailsBox: {
    marginLeft: '26px',
    marginTop: '8px',
    padding: '8px',
    background: 'var(--cg-neutral-50)',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-1)',
    overflow: 'auto',
    maxHeight: '200px',
  },
  detailsPre: {
    margin: 0,
    fontSize: '11px',
    color: 'var(--fg-2)',
    fontFamily: 'var(--font-mono)',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
  },
  approveSection: {
    marginTop: '16px',
    paddingTop: '12px',
    borderTop: '1px solid var(--border-1)',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-end',
    gap: '8px',
  },
  blockerMessage: {
    margin: 0,
    fontSize: '12px',
    color: 'var(--danger)',
    textAlign: 'right' as const,
    width: '100%',
  },
  warningMessage: {
    margin: 0,
    fontSize: '12px',
    color: 'var(--warning)',
    textAlign: 'right' as const,
    width: '100%',
  },
  loadingText: {
    display: 'flex',
    justifyContent: 'center',
    padding: '12px',
  },
  errorText: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--danger)',
    background: 'var(--danger-soft)',
    border: '1px solid var(--cg-terra-200)',
    borderRadius: 'var(--radius-md)',
    padding: '10px 12px',
    marginBottom: '8px',
  },
};

export default ValidationPreview;
